import { useState } from "react";
import { Mail, Phone, MapPin, Send, Facebook, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

// API URL - uses relative path for Vercel, or env variable for development
const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

const contactInfo = [
  {
    icon: Mail,
    label: "Email",
    value: "lataigasiberia@gmail.com",
    href: "mailto:lataigasiberia@gmail.com",
  },
  {
    icon: MapPin,
    label: "Localisation",
    value: "France",
    href: null,
  },
];

const socialLinks = [
  {
    icon: Facebook,
    label: "Facebook",
    href: "https://www.facebook.com/profile.php?id=100094255166375",
    color: "hover:bg-blue-600",
  },
  {
    icon: Instagram,
    label: "Instagram",
    href: "https://www.instagram.com/la_taiga_siberia",
    color: "hover:bg-pink-600",
  },
];

const subjects = [
  { value: "information", label: "Demande d'information générale" },
  { value: "chaton", label: "Adoption d'un chaton" },
  { value: "visite", label: "Demande de visite" },
  { value: "autre", label: "Autre" },
];

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    interested_in: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Mode statique - redirection vers email
    const subject = encodeURIComponent(subjects.find(s => s.value === formData.subject)?.label || formData.subject);
    const body = encodeURIComponent(`Nom: ${formData.name}\nTéléphone: ${formData.phone || 'Non renseigné'}\n\nMessage:\n${formData.message}`);
    
    window.location.href = `mailto:lataigasiberia@gmail.com?subject=${subject}&body=${body}`;
    
    toast.success("Redirection vers votre messagerie...");
    setIsSubmitting(false);
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 lg:py-32 bg-cream-alt overflow-hidden"
    >
      {/* Decorative background */}
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-sand/50 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section header */}
        <div className="section-animate text-center mb-16">
          <span className="font-accent text-autumn text-xl">Parlons ensemble</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-forest mt-2 mb-6">
            Contactez-nous
          </h2>
          <p className="font-body text-lg text-forest/70 max-w-2xl mx-auto">
            Une question sur nos chatons, notre élevage ou la race Sibérien ?
            N'hésitez pas à nous écrire, nous vous répondrons avec plaisir.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact info */}
          <div className="lg:col-span-2 section-animate">
            <div className="bg-forest rounded-3xl p-8 text-white h-full">
              <h3 className="font-heading text-2xl font-semibold mb-6">
                Nos coordonnées
              </h3>

              <div className="space-y-6 mb-8">
                {contactInfo.map((info) => (
                  <div key={info.label} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-5 h-5 text-sand" />
                    </div>
                    <div>
                      <span className="font-body text-xs text-sand/70 uppercase tracking-wider">
                        {info.label}
                      </span>
                      {info.href ? (
                        <a
                          href={info.href}
                          className="font-body text-cream hover:text-autumn transition-colors block"
                          data-testid={`contact-${info.label.toLowerCase()}`}
                        >
                          {info.value}
                        </a>
                      ) : (
                        <p className="font-body text-cream">{info.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Social links */}
              <div className="pt-6 border-t border-white/10">
                <h4 className="font-body text-sm font-semibold text-sand mb-4">
                  Suivez-nous
                </h4>
                <div className="flex gap-3">
                  {socialLinks.map((social) => (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center ${social.color} transition-colors`}
                      data-testid={`social-${social.label.toLowerCase()}`}
                    >
                      <social.icon className="w-5 h-5" />
                    </a>
                  ))}
                </div>
              </div>

              {/* Image */}
              <div className="mt-8">
                <img
                  src="https://images.unsplash.com/photo-1690177930780-6b3c800a2f57?auto=format&fit=crop&w=400&q=80"
                  alt="Chat Sibérien"
                  className="rounded-2xl w-full h-40 object-cover opacity-80"
                />
              </div>
            </div>
          </div>

          {/* Contact form */}
          <div className="lg:col-span-3 section-animate">
            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-3xl p-8 shadow-soft"
              data-testid="contact-form"
            >
              <h3 className="font-heading text-2xl font-semibold text-forest mb-6">
                Envoyez-nous un message
              </h3>

              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label htmlFor="contact-name" className="font-body text-sm font-semibold text-forest">
                    Nom complet *
                  </Label>
                  <Input
                    id="contact-name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 bg-cream border-sand focus:border-forest focus:ring-forest"
                    placeholder="Votre nom"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-email" className="font-body text-sm font-semibold text-forest">
                    Email *
                  </Label>
                  <Input
                    id="contact-email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1 bg-cream border-sand focus:border-forest focus:ring-forest"
                    placeholder="votre@email.com"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label htmlFor="contact-phone" className="font-body text-sm font-semibold text-forest">
                    Téléphone
                  </Label>
                  <Input
                    id="contact-phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="mt-1 bg-cream border-sand focus:border-forest focus:ring-forest"
                    placeholder="06 XX XX XX XX"
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-subject" className="font-body text-sm font-semibold text-forest">
                    Sujet *
                  </Label>
                  <Select
                    value={formData.subject}
                    onValueChange={(value) => setFormData({ ...formData, subject: value })}
                    required
                  >
                    <SelectTrigger 
                      className="mt-1 bg-cream border-sand focus:border-forest focus:ring-forest"
                      data-testid="contact-subject-select"
                    >
                      <SelectValue placeholder="Sélectionnez un sujet" />
                    </SelectTrigger>
                    <SelectContent>
                      {subjects.map((subject) => (
                        <SelectItem key={subject.value} value={subject.value}>
                          {subject.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mb-6">
                <Label htmlFor="contact-message" className="font-body text-sm font-semibold text-forest">
                  Message *
                </Label>
                <Textarea
                  id="contact-message"
                  required
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="mt-1 bg-cream border-sand focus:border-forest focus:ring-forest resize-none"
                  placeholder="Décrivez votre demande..."
                  rows={5}
                  data-testid="contact-message-input"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="relative w-full bg-forest hover:bg-forest-dark text-white font-body font-semibold py-6 rounded-full transition-all duration-300 z-10"
                data-testid="contact-submit-btn"
              >
                <Send className="w-5 h-5 mr-2" />
                {isSubmitting ? "Envoi en cours..." : "Envoyer le message"}
              </Button>

              <p className="font-body text-xs text-forest/50 mt-4 text-center">
                Nous vous répondrons dans les meilleurs délais, généralement sous 48h.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
